import { format as formatSql } from 'sql-formatter';

import type {
  HealthOverall,
  MetricLevel,
  ScorecardStatus,
  ScorecardUIStatus,
} from './types';
import { isMultiEnvEnabled, isProduction } from '@/common/environment';
import { SystemEnvironment } from '@/constants/systemEnviroment';
import { getSystemEnvironment } from '@/utils/system-environment';

export const SGT_TIMEZONE = 'Asia/Singapore';

const MIDAS_BASE_URL_STAGING = 'https://console.stg-myteksi.com/midas';
const MIDAS_BASE_URL_PRODUCTION = 'https://console.grab.com/midas';

const getMidasBaseUrl = (): string => {
  const isProd =
    isProduction ||
    (isMultiEnvEnabled() &&
      getSystemEnvironment() === SystemEnvironment.PRODUCTION);
  return isProd ? MIDAS_BASE_URL_PRODUCTION : MIDAS_BASE_URL_STAGING;
};

export const getMidasMetricUrl = (metricKey: string): string =>
  `${getMidasBaseUrl()}/metrics/${metricKey}/definition`;

const DEFAULT_ALPHA = 0.05;
const PERCENTAGE_METRIC_UNIT = 'percent';

export const ScorecardStatusValue = {
  SUCCEEDED: 'SUCCEEDED',
  MIDAS_QUEUED: 'MIDAS_QUEUED',
  SCORECARD_PROCESSING: 'SCORECARD_PROCESSING',
  MIDAS_FAILED: 'MIDAS_FAILED',
  SCORECARD_FAILED: 'SCORECARD_FAILED',
  NOT_STARTED: 'NOT_STARTED',
  SCORECARD_QUEUED: 'SCORECARD_QUEUED',
} as const;

export const getSgtDateKey = (date: Date): string =>
  date.toLocaleDateString('en-CA', { timeZone: SGT_TIMEZONE });

export const formatDate = (dateString: string | null): string => {
  if (!dateString) return '—';

  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: SGT_TIMEZONE,
  });
};

export const formatFullDate = (dateString: string | null): string => {
  if (!dateString) return '—';

  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: SGT_TIMEZONE,
  });
};

export const formatDateTime = (timestamp: string | null): string => {
  if (!timestamp) return '—';

  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: SGT_TIMEZONE,
  });
};

export const formatRelativeTime = (timestamp: string | null): string => {
  if (!timestamp) return 'Not yet run';

  const diffHrs = Math.floor(
    (Date.now() - new Date(timestamp).getTime()) / (1000 * 60 * 60),
  );

  if (diffHrs < 0) {
    const abs = Math.abs(diffHrs);
    return abs < 24 ? `in ${abs} hrs` : `in ${Math.floor(abs / 24)} days`;
  }

  if (diffHrs < 1) return 'Just now';
  if (diffHrs < 24) return `${diffHrs} hrs ago`;
  return `${Math.floor(diffHrs / 24)} days ago`;
};

export const formatAbsoluteTime = (timestamp: string | null): string => {
  if (!timestamp) return 'Experiment just started';

  const date = new Date(timestamp);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dateKey = getSgtDateKey(date);
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: SGT_TIMEZONE,
  });

  if (dateKey === getSgtDateKey(now)) return `Today ${timeStr} SGT`;
  if (dateKey === getSgtDateKey(tomorrow)) return `Tomorrow ${timeStr} SGT`;
  return `${formatDate(timestamp)} ${timeStr} SGT`;
};

export const calculateDaysDiff = (
  startDate: string | null,
  endDate: string | null,
): number => {
  if (!startDate || !endDate) return 0;

  return Math.floor(
    (new Date(endDate).getTime() - new Date(startDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );
};

const formatNumber = (value: number, fractionDigits = 4): string =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(value);

const formatSignedNumber = (value: number, fractionDigits = 4): string => {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${formatNumber(Math.abs(value), fractionDigits)}`;
};

export const isPercentageMetric = ({
  metricUnit,
}: {
  metricUnit?: string | null;
}): boolean => {
  return metricUnit?.toLowerCase() === PERCENTAGE_METRIC_UNIT;
};

export const formatMetricMean = (
  value: number | null | undefined,
  isPercentageMetric: boolean,
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (isPercentageMetric) return `${(value * 100).toFixed(2)}%`;
  return formatNumber(value);
};

export const formatAbsoluteDelta = ({
  controlMean,
  treatmentMean,
  isPercentageMetric,
}: {
  controlMean?: number | null;
  treatmentMean?: number | null;
  isPercentageMetric: boolean;
}): string => {
  if (
    controlMean === null ||
    controlMean === undefined ||
    treatmentMean === null ||
    treatmentMean === undefined
  ) {
    return '—';
  }

  const delta = treatmentMean - controlMean;

  if (isPercentageMetric) {
    return `${formatSignedNumber(delta * 100, 2)} pp`;
  }

  return formatSignedNumber(delta);
};

export const formatRelativeDelta = (uplift: number | null): string => {
  if (uplift === null || Number.isNaN(uplift)) return '—';
  return `${formatSignedNumber(uplift * 100, 2)}%`;
};

export const formatUplift = (uplift: number | null): string =>
  formatRelativeDelta(uplift);

export const calculateRelativeUplift = (
  controlMean: number | null | undefined,
  treatmentMean: number | null | undefined,
): number | null => {
  if (
    controlMean == null ||
    treatmentMean == null ||
    Number.isNaN(controlMean) ||
    Number.isNaN(treatmentMean) ||
    controlMean === 0
  ) {
    return null;
  }
  return (treatmentMean - controlMean) / controlMean;
};

export const formatPValue = (pValue: number | null | undefined): string => {
  if (pValue === null || pValue === undefined || Number.isNaN(pValue)) {
    return '—';
  }
  if (pValue < 0.001) return '<0.001';
  return pValue.toFixed(3);
};

export const formatConfidenceInterval = ({
  ciLower,
  ciUpper,
  isPercentageMetric,
}: {
  ciLower?: number | null;
  ciUpper?: number | null;
  isPercentageMetric: boolean;
}): string => {
  if (
    ciLower === null ||
    ciLower === undefined ||
    ciUpper === null ||
    ciUpper === undefined
  ) {
    return '—';
  }

  if (isPercentageMetric) {
    return `[${formatSignedNumber(ciLower * 100, 2)} pp, ${formatSignedNumber(
      ciUpper * 100,
      2,
    )} pp]`;
  }

  return `[${formatSignedNumber(ciLower)}, ${formatSignedNumber(ciUpper)}]`;
};

export const getAdjustedAlpha = (testCount: number): number =>
  DEFAULT_ALPHA / Math.max(testCount, 1);

export const isStatisticallySignificant = (
  pValue: number | null | undefined,
  testCount: number,
): boolean | null => {
  if (pValue === null || pValue === undefined || Number.isNaN(pValue)) {
    return null;
  }
  return pValue <= getAdjustedAlpha(testCount);
};

export const getSignificanceLabel = (
  pValue: number | null | undefined,
  testCount: number,
): { label: string; color: string } => {
  const isSignificant = isStatisticallySignificant(pValue, testCount);

  if (isSignificant === null) {
    return { label: '—', color: 'default' };
  }

  return isSignificant
    ? { label: 'YES', color: 'green' }
    : { label: 'NO', color: 'red' };
};

export const getDeltaColor = (delta: number | null | undefined): string => {
  if (delta === null || delta === undefined || Number.isNaN(delta)) {
    return '#595959';
  }
  if (delta > 0) return '#237804';
  if (delta < 0) return '#cf1322';
  return '#595959';
};

export const formatSqlForDisplay = (sql: string): string => {
  if (!sql) return '—';

  try {
    return formatSql(sql, {
      language: 'trino',
      keywordCase: 'upper',
      tabWidth: 2,
    });
  } catch {
    return sql.trim() || '—';
  }
};

export const METRIC_LEVEL_TAG: Record<
  MetricLevel,
  { label: string; color: string }
> = {
  P0: { label: 'P0', color: 'red' },
  P1: { label: 'P1', color: 'blue' },
  G: { label: 'Guardrail', color: 'cyan' },
};

export const STATUS_BADGE: Record<
  ScorecardUIStatus,
  {
    status: 'success' | 'warning' | 'processing' | 'error' | 'default';
    label: string;
  }
> = {
  SUCCEEDED: { status: 'success', label: 'Success' },
  MIDAS_QUEUED: {
    status: 'warning',
    label: 'Waiting for upstream metric data',
  },
  SCORECARD_PROCESSING: {
    status: 'processing',
    label: 'Inprogress - Computing scorecard',
  },
  MIDAS_FAILED: { status: 'error', label: 'Failed' },
  SCORECARD_FAILED: { status: 'error', label: 'Failed' },
  NOT_STARTED: { status: 'default', label: 'Not started' },
  SCORECARD_QUEUED: { status: 'processing', label: 'Queued' },
  TRIGGERED_IN_PROGRESS: {
    status: 'processing',
    label: 'AXA scorecard triggered (In progress)',
  },
};

export const HEALTH_CONFIG: Record<
  HealthOverall | 'default',
  { label: string; dotColor: string; borderColor: string; bgColor: string }
> = {
  healthy: {
    label: 'Healthy',
    dotColor: '#52c41a',
    borderColor: '#b7eb8f',
    bgColor: '#f6ffed',
  },
  failing: {
    label: 'Failing',
    dotColor: '#ff4d4f',
    borderColor: '#ffccc7',
    bgColor: '#fff1f0',
  },
  in_progress: {
    label: 'In progress',
    dotColor: '#1890ff',
    borderColor: '#91d5ff',
    bgColor: '#e6f7ff',
  },
  default: {
    label: 'Unknown',
    dotColor: '#8c8c8c',
    borderColor: '#d9d9d9',
    bgColor: '#fafafa',
  },
};

const TRIGGERED_STATUS_THRESHOLD_HOURS = 12;

const isWithinTriggeredWindow = (createdAt: string | null): boolean => {
  if (!createdAt) return false;
  const createdTime = new Date(createdAt).getTime();
  const now = Date.now();
  const diffHours = (now - createdTime) / (1000 * 60 * 60);
  return diffHours >= 0 && diffHours <= TRIGGERED_STATUS_THRESHOLD_HOURS;
};

export const getStatusBadge = (status: string, createdAt?: string | null) => {
  const scorecardStatus = status as ScorecardStatus;

  if (
    scorecardStatus === 'MIDAS_QUEUED' &&
    createdAt &&
    isWithinTriggeredWindow(createdAt)
  ) {
    return STATUS_BADGE.TRIGGERED_IN_PROGRESS;
  }

  return (
    STATUS_BADGE[scorecardStatus] ?? {
      status: 'default' as const,
      label: status,
    }
  );
};

export const getMetricLevelTag = (level: string) =>
  METRIC_LEVEL_TAG[level as MetricLevel] ?? { label: level, color: 'default' };

export const getHealthConfig = (
  overall: string | null | undefined,
  lastRunAt?: string | null,
) => {
  if (!overall) {
    return { ...HEALTH_CONFIG.default, label: 'Unknown' };
  }
  const config = HEALTH_CONFIG[overall as HealthOverall];
  if (!config) {
    return { ...HEALTH_CONFIG.default, label: overall };
  }
  if (overall === 'in_progress') {
    return {
      ...config,
      label: lastRunAt ? 'In progress' : 'Awaiting first run',
    };
  }
  return config;
};

export const getApiErrorArg = (error: unknown): string | null => {
  if (!error || typeof error !== 'object') return null;

  const e = error as {
    response?: { data?: { devMessage?: string; error?: string } };
  };

  return e.response?.data?.devMessage ?? e.response?.data?.error ?? null;
};

export const getApiErrorStatus = (error: unknown): number | null => {
  if (!error || typeof error !== 'object') return null;

  const e = error as {
    response?: { status?: number };
  };

  return e.response?.status ?? null;
};

export const getScorecardErrorMessage = (error: unknown): string => {
  const code = getApiErrorArg(error);

  if (code === 'experiment_not_found') return 'Experiment not found.';
  if (code === 'not_on_demand_experiment') {
    return 'This experiment has no on-demand AXA metrics configured.';
  }

  return 'Failed to load scorecard. Please try again later.';
};

const NEW_METRIC_THRESHOLD_HOURS = 2;

export const isNewMetric = (createdAt: string | null): boolean => {
  if (!createdAt) return false;
  // API returns UTC timestamps (e.g., "2026-05-07T03:19:53Z")
  // Both Date.parse and Date.now() work in UTC, so comparison is timezone-agnostic
  const createdTime = new Date(createdAt).getTime();
  const now = Date.now();
  const diffHours = (now - createdTime) / (1000 * 60 * 60);
  return diffHours >= 0 && diffHours <= NEW_METRIC_THRESHOLD_HOURS;
};

export const getMetricScorecardErrorMessage = (error: unknown): string => {
  const status = getApiErrorStatus(error);
  const code = getApiErrorArg(error);

  if (status === 404) return 'Scorecard not available yet for this metric.';
  if (code?.startsWith('no_results_yet')) {
    return 'No results yet — computation is still running. Check back after the next scheduled run.';
  }
  if (code === 'metric_not_in_experiment') {
    return 'This metric is no longer tracked by this experiment.';
  }
  if (code === 'experiment_not_found') return 'Experiment not found.';

  return 'Failed to load scorecard data.';
};

export const getMetricQueryErrorMessage = (error: unknown): string => {
  const code = getApiErrorArg(error);

  if (code === 'no_query_available') {
    return 'No successful SQL query is available for this metric yet.';
  }
  if (code === 'dimension_not_found') {
    return 'No SQL query was recorded for this dimension.';
  }

  return 'Failed to load SQL query preview.';
};
